import React, { useCallback, useState } from 'react';
import { Input, Button, Modal, Spin, Select, InputNumber, DatePicker, Table, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useInvoiceRequestForm } from './hooks/useInvoiceRequestForm';
import { invoiceRequestApi, STATUS_MAP, INVOICE_TYPE_MAP } from '../../services/invoiceRequestApi';
import { BatchAcceptanceBridge, pwg209f1AcceptanceManifest } from '../../features/acceptance';
import dayjs from 'dayjs';
import { useModulePermission } from '../../hooks/usePermission';

const { Option } = Select;

const G209Form: React.FC = () => {
  const permissions = useModulePermission('pwg209f1');
  const {
    mode, isEditing, isDraft, loading,
    records, current, currentIdx,
    master, details,
    updateMaster, addDetailRow, removeDetailRow, updateDetail,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, doSubmit, doApprove, quickSearch,
  } = useInvoiceRequestForm(permissions.canView);

  const [searchText, setSearchText] = useState('');

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除申請單「${current.fg160_01}」？`,
      okText: '刪除', okType: 'danger', cancelText: '取消',
      onOk: doDelete,
    });
  };

  const handleSubmit = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定要送審申請單「${current.fg160_01}」？`,
      okText: '送審', cancelText: '取消',
      onOk: doSubmit,
    });
  };

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';
  const statusInfo = STATUS_MAP[current?.fg160_80 || 'A'] || STATUS_MAP['A'];
  const queryAcceptanceExact = useCallback((id: string) => invoiceRequestApi.get(id), []);

  const detailColumns = [
    { title: <span data-contract-field="FG161_02">項次</span>, dataIndex: 'fg161_02', width: 56 },
    {
      title: <span data-contract-field="FG161_22">發票品名</span>, dataIndex: 'fg161_22', width: 180,
      render: (v: string, _: any, i: number) => isEditing
        ? <Input size="small" value={v} onChange={e => updateDetail(i, 'fg161_22', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="FG161_17">未稅開票金額</span>, dataIndex: 'fg161_17', width: 120,
      render: (v: number, _: any, i: number) => isEditing
        ? <InputNumber size="small" value={v} min={0} onChange={val => updateDetail(i, 'fg161_17', val || 0)} style={{ width: '100%' }} />
        : v?.toLocaleString(),
    },
    {
      title: <span data-contract-field="FG161_18">含稅開票金額</span>, dataIndex: 'fg161_18', width: 120,
      render: (v: number, _: any, i: number) => isEditing
        ? <InputNumber size="small" value={v} min={0} onChange={val => updateDetail(i, 'fg161_18', val || 0)} style={{ width: '100%' }} />
        : v?.toLocaleString(),
    },
    {
      title: <span data-contract-field="FG161_21">收入／費用代號</span>, dataIndex: 'fg161_21', width: 120,
      render: (v: string, _: any, i: number) => isEditing
        ? <Input size="small" value={v} onChange={e => updateDetail(i, 'fg161_21', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="FG161_26">S/C NO.／樣品申請單號</span>, dataIndex: 'fg161_26', width: 160,
      render: (v: string, _: any, i: number) => isEditing
        ? <Input size="small" value={v} onChange={e => updateDetail(i, 'fg161_26', e.target.value)} />
        : v,
    },
    ...(isEditing ? [{
      title: '', width: 40,
      render: (_: any, __: any, i: number) => (
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeDetailRow(i)} />
      ),
    }] : []),
  ];

  return (
    <Spin spinning={loading}>
      <div className="max-w-5xl mx-auto p-6">
        {/* Title */}
        <h2 className="text-xl font-bold mb-4">
          開立發票申請單 (pwg209f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
          {current && (
            <Tag color={statusInfo.color} className="ml-2">{statusInfo.label}</Tag>
          )}
        </h2>

        {/* Search & Navigation */}
        <div className="flex items-center gap-2 mb-4">
          <Input placeholder="單號/客戶搜尋" value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={() => quickSearch(searchText.trim())}
            disabled={isEditing || !permissions.canView} style={{ width: 240 }} />
          <Button onClick={() => quickSearch(searchText.trim())} disabled={isEditing || !permissions.canView}>查詢</Button>
          <div className="ml-4 flex gap-1">
            <Button size="small" onClick={navFirst} disabled={!permissions.canView || isEditing || records.length === 0}>|&lt;</Button>
            <Button size="small" onClick={navPrev} disabled={!permissions.canView || isEditing || currentIdx <= 0}>&lt;</Button>
            <Button size="small" onClick={navNext} disabled={!permissions.canView || isEditing || currentIdx >= records.length - 1}>&gt;</Button>
            <Button size="small" onClick={navLast} disabled={!permissions.canView || isEditing || records.length === 0}>&gt;|</Button>
          </div>
        </div>

        {/* Master Fields */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1"><span data-contract-field="FG160_01">申請單號</span></label>
            <Input data-contract-field="FG160_01" value={master.fg160_01 || '(自動產生)'} disabled style={{ width: '100%' }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <span data-contract-field="FG160_11">申請日期</span> <span className="text-red-500">*</span>
            </label>
            <DatePicker
              data-contract-field="FG160_11"
              value={master.fg160_11 ? dayjs(master.fg160_11) : null}
              onChange={d => updateMaster('fg160_11', d?.format('YYYY-MM-DD') || null)}
              disabled={!isEditing} style={{ width: '100%' }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <span data-contract-field="FG160_24">開票類別</span> <span className="text-red-500">*</span>
            </label>
            <Select data-contract-field="FG160_24" value={master.fg160_24 || 'F'}
              onChange={v => updateMaster('fg160_24', v)}
              disabled={!isEditing} style={{ width: '100%' }}>
              {Object.entries(INVOICE_TYPE_MAP).map(([k, v]) => (
                <Option key={k} value={k}>{v}</Option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <span data-contract-field="FG160_18">客戶代號</span> <span className="text-red-500">*</span>
            </label>
            <Input data-contract-field="FG160_18" value={master.fg160_18 || ''}
              onChange={e => updateMaster('fg160_18', e.target.value)}
              disabled={!isEditing} maxLength={12} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1"><span data-contract-field="FG160_15">開票對象</span></label>
            <Input data-contract-field="FG160_15" value={master.fg160_15 || ''}
              onChange={e => updateMaster('fg160_15', e.target.value)}
              disabled={!isEditing} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1"><span data-contract-field="FG160_19">幣別</span></label>
            <Input data-contract-field="FG160_19" value={master.fg160_19 || ''}
              onChange={e => updateMaster('fg160_19', e.target.value)}
              disabled={!isEditing} maxLength={3} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1"><span data-contract-field="FG160_13">傳送方式</span></label>
            <Select data-contract-field="FG160_13" value={master.fg160_13 || 1}
              onChange={v => updateMaster('fg160_13', v)}
              disabled={!isEditing} style={{ width: '100%' }}>
              <Option value={1}>郵寄</Option>
              <Option value={2}>自取</Option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <span data-contract-field="FG160_21">發票匯率</span> <span className="text-red-500">*</span>
            </label>
            <InputNumber data-contract-field="FG160_21" value={master.fg160_21 || 1.0}
              onChange={v => updateMaster('fg160_21', v || 1.0)}
              disabled={!isEditing} min={0.0001} step={0.01} style={{ width: '100%' }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1"><span data-contract-field="FG160_14">發票種類</span></label>
            <Select data-contract-field="FG160_14" value={master.fg160_14 || 1}
              onChange={v => updateMaster('fg160_14', v)}
              disabled={!isEditing} style={{ width: '100%' }}>
              <Option value={1}>增值稅-三聯式</Option>
              <Option value={2}>普通發票-二聯式</Option>
              <Option value={3}>外銷發票-零稅率</Option>
              <Option value={4}>不開票-內銷</Option>
              <Option value={5}>不開票-外銷</Option>
            </Select>
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1"><span data-contract-field="FG160_32">備註</span></label>
            <Input data-contract-field="FG160_32" value={master.fg160_32 || ''}
              onChange={e => updateMaster('fg160_32', e.target.value)}
              disabled={!isEditing} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1"><span data-contract-field="FG160_17">部門代號</span></label>
            <Input data-contract-field="FG160_17" value={master.fg160_17 || ''}
              onChange={e => updateMaster('fg160_17', e.target.value)}
              disabled={!isEditing} maxLength={4} />
          </div>
        </div>

        {/* Detail Table */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base font-semibold">明細項目</h3>
            {isEditing && (
              <Button size="small" icon={<PlusOutlined />} onClick={addDetailRow}>新增明細</Button>
            )}
          </div>
          <Table
            dataSource={details.map((d, i) => ({ ...d, key: i }))}
            columns={detailColumns as any}
            pagination={false}
            size="small"
            bordered
          />
        </div>

        {/* Summary */}
        {current && mode === 'browse' && (
          <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-gray-50 rounded">
            <div>
              <span className="text-sm text-gray-500" data-contract-field="FG160_22">發票淨額：</span>
              <span className="font-semibold">{(master.fg160_22 || 0).toLocaleString()}</span>
            </div>
            <div>
              <span className="text-sm text-gray-500" data-contract-field="FG160_27">稅額：</span>
              <span className="font-semibold">{(master.fg160_27 || 0).toLocaleString()}</span>
            </div>
            <div>
              <span className="text-sm text-gray-500" data-contract-field="FG160_23">發票總額：</span>
              <span className="font-semibold">{(master.fg160_23 || 0).toLocaleString()}</span>
            </div>
          </div>
        )}

        {/* Audit Info */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-4 space-y-1">
            <div><span data-contract-field="FG160_12">入單人員</span>: {current.fg160_12} | <span data-contract-field="FG160_91">最後異動人員</span>: {current.fg160_91}</div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button type="primary" data-acceptance-action="PWG209-ACT-CREATE" onClick={startAdd} disabled={!permissions.canCreate}>新增</Button>
              <Button data-acceptance-action="PWG209-ACT-UPDATE" onClick={startEdit} disabled={!permissions.canEdit || !current || !isDraft}>修改</Button>
              <Button danger data-acceptance-action="PWG209-ACT-DELETE" onClick={handleDelete} disabled={!permissions.canDelete || !current || !isDraft}>刪除</Button>
              {current?.fg160_80 === 'A' && (
                <Button data-acceptance-action="PWG209-ACT-SUBMIT" onClick={handleSubmit} disabled={!permissions.canEdit}>送審</Button>
              )}
              {(current?.fg160_80 === 'E' || current?.fg160_80 === 'X') && (
                <Button type="primary" data-acceptance-action="PWG209-ACT-APPROVE" onClick={doApprove} disabled={!permissions.canApprove}>核准</Button>
              )}
            </>
          ) : (
            <>
              <Button type="primary" onClick={save} disabled={mode === 'add' ? !permissions.canCreate : !permissions.canEdit}>儲存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>
        <BatchAcceptanceBridge
          manifest={pwg209f1AcceptanceManifest}
          records={records}
          selectedKeys={current ? [current.fg160_01] : []}
          keyOf={(item) => item.fg160_01}
          queryExact={queryAcceptanceExact}
          lastApiResponse={current}
          loading={loading}
          onAutoFill={() => { if (current && !isEditing && isDraft) startEdit(); }}
          autoFillDisabled={!current || isEditing || !isDraft}
          autoFillReason="請先載入一張可修改的既有申請單；自動填表只會複製目前 API 資料到草稿，不會送出。"
          screenDraft={{ master, details }}
        />
      </div>
    </Spin>
  );
};

export default G209Form;
